import React from 'react';

import { useGetPatientClinicalInfoQuery } from '../../../../store';
import type { PatientClinicalInfo } from '../../../../types';

import { ClinicalDetailsSkeleton } from './ClinicalDetailsSkeleton';
import { LastSessionSummary } from './LastSessionSummary';
import { PatientOverview } from './PatientOverview';

interface PatientClinicalDetailsProps {
  patientId: string;
  expanded: boolean;
}

/**
 * Container component for patient clinical details
 * Fetches clinical information when expanded and displays overview + last session
 * Only fetches once per component lifecycle to avoid unnecessary API calls
 */
export const PatientClinicalDetails: React.FC<PatientClinicalDetailsProps> = ({
  patientId,
  expanded,
}) => {
  // Use the query hook which will automatically fetch when expanded
  const { data, isLoading, error } = useGetPatientClinicalInfoQuery(
    { patientId },
    {
      // Only fetch when expanded and we have a patient ID
      skip: !expanded || !patientId,
    },
  );

  // Extract the clinical data from the response
  const clinicalData: PatientClinicalInfo | null = data?.patient_clinical_info || null;

  // Show skeleton while loading
  if (isLoading) {
    return <ClinicalDetailsSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <>
        <PatientOverview clinicalInfo={null} patientId={patientId} />
      </>
    );
  }

  // Show placeholder if no data after loading
  if (!isLoading && !clinicalData && expanded) {
    return (
      <>
        <PatientOverview clinicalInfo={null} patientId={patientId} />
      </>
    );
  }

  // Show actual data
  if (clinicalData) {
    return (
      <>
        <PatientOverview clinicalInfo={clinicalData} patientId={patientId} />
        <LastSessionSummary
          lastSessionSummary={clinicalData.last_session?.summary}
          lastSessionDate={clinicalData.last_session?.date_completed}
        />
      </>
    );
  }

  // Default placeholder before fetching
  return (
    <>
      <PatientOverview clinicalInfo={null} patientId={patientId} />
      <LastSessionSummary lastSessionSummary={null} lastSessionDate={null} />
    </>
  );
};
